import React, { useState } from 'react';
import { Brain, Lightbulb, Zap, BookOpen, GraduationCap, Target, Users, Award, Clock, Download, Eye, BarChart3, FileText, Globe, Rocket, CheckCircle, Star, ArrowRight, Play, Shield, Sparkles } from 'lucide-react';

const RightMathsBrain = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: '系統概覽', icon: Eye },
    { id: 'teachers', label: '教師專區', icon: Users },
    { id: 'students', label: '學生專區', icon: GraduationCap },
    { id: 'hkdse', label: 'HKDSE備試', icon: Target },
    { id: 'primary', label: '小學數學', icon: BookOpen },
    { id: 'assessment', label: '自我評估', icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <Brain className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white">
                數學右腦 RightMaths
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-8">
              使用AI驅動的數學學習系統，掌握HKDSE數學課程的右腦思維方法
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300 flex items-center justify-center space-x-2">
                <span>立即開始</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 flex items-center justify-center space-x-2">
                <Play className="w-5 h-5" />
                <span>觀看演示</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            為教育工作者設計的最佳AI
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            其他AI無法解決的問題，RightMaths都能完美解決 — 自定義聊天機器人、教學資源、閃卡、互動測驗、幻燈片生成器，我們精確地填補您需要的空白。
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Feature Card 1 */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-4 w-16 h-16 flex items-center justify-center mb-6">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">知識圖譜可視化</h3>
            <p className="text-gray-600 leading-relaxed">
              3D互動式概念關係圖，讓抽象的數學概念變得具體易懂，激發學生的數學直覺和創造力。
            </p>
          </div>

          {/* Feature Card 2 */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div className="bg-gradient-to-br from-green-500 to-blue-600 rounded-lg p-4 w-16 h-16 flex items-center justify-center mb-6">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">AI練習題生成</h3>
            <p className="text-gray-600 leading-relaxed">
              智能生成個性化練習題，根據學習進度調整難度，提供即時反饋與詳細解答。
            </p>
          </div>

          {/* Feature Card 3 */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg p-4 w-16 h-16 flex items-center justify-center mb-6">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">進度追蹤分析</h3>
            <p className="text-gray-600 leading-relaxed">
              詳細學習報告與分析，識別強弱項，提供個人化學習建議，幫助學生持續改進。
            </p>
          </div>

          {/* Feature Card 4 */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-lg p-4 w-16 h-16 flex items-center justify-center mb-6">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">HKDSE備試系統</h3>
            <p className="text-gray-600 leading-relaxed">
              專為中四至中六學生設計的完整備試系統，包含歷年試題庫和智能練習系統。
            </p>
          </div>

          {/* Feature Card 5 */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div className="bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg p-4 w-16 h-16 flex items-center justify-center mb-6">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">小學數學專區</h3>
            <p className="text-gray-600 leading-relaxed">
              為小學生提供基礎數學學習平台，遊戲化學習元素，為升中做好充分準備。
            </p>
          </div>

          {/* Feature Card 6 */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-4 w-16 h-16 flex items-center justify-center mb-6">
              <Award className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">成就系統</h3>
            <p className="text-gray-600 leading-relaxed">
              學習成就徽章收集，學習排行榜激勵，記錄學習里程碑，持續激發學習動力。
            </p>
          </div>
        </div>
      </div>

      {/* Bring Your Own Knowledge Section */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                帶入您自己的知識
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                上傳您的教學材料，讓RightMaths分析它們，並輕鬆生成與您的教學目標一致的內容。
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="text-gray-700">智能內容分析</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="text-gray-700">個性化教學資源</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="text-gray-700">自動化內容生成</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-6 text-center">
                <FileText className="w-16 h-16 text-white mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">教學資源庫</h3>
                <p className="text-blue-100">豐富的HKDSE題庫資源，歷年試題分析與解答</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive AI Section */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="bg-gradient-to-br from-green-500 to-blue-600 rounded-lg p-6 text-center">
                <Sparkles className="w-16 h-16 text-white mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">智能AI互動</h3>
                <p className="text-green-100">與學生互動的智能AI，從創建到反饋和洞察的完全個性化</p>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                與學生互動的智能AI
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                超越基於文本的輸出，RightMaths讓您為學生生成互動內容，從創建到反饋和洞察的完全個性化。
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="text-gray-700">即時學習反饋</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="text-gray-700">個性化學習路徑</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="text-gray-700">智能進度追蹤</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">教師們怎麼說</h2>
            <p className="text-xl text-gray-600">了解為什麼教育工作者轉向RightMaths尋求更好的教學解決方案</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                  S
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Samantha P.</h4>
                  <p className="text-sm text-gray-600">中學數學教師</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "RightMaths完全改變了我備課的方式。所有可用的教學工具選項都是遊戲規則改變者，每週為我節省數小時。現在無法想像沒有它如何教學！"
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                  I
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Ibrahim G.</h4>
                  <p className="text-sm text-gray-600">小學數學教師</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "平台的AI驅動功能幫助我分析學生需求並調整教學方法，豐富的材料和資源使我能夠以新的、更吸引人的方式吸引學生。"
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                  A
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Alicia R.</h4>
                  <p className="text-sm text-gray-600">HKDSE數學教師</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "RightMaths將內容與我的課程對齊的能力讓我的生活變得如此輕鬆。資源恰到好處，我的學生喜歡互動課程。這是我遇到的最好的教學工具。"
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Security & Compliance Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
              <h2 className="text-4xl font-bold text-gray-900">我們對安全與合規的承諾</h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              RightMaths AI完全符合SOC 2、GDPR、FERPA、COPPA，確保您的信息受到最高行業標準的保護。
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-lg p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">SOC 2 合規</h3>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-lg p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">FERPA 合規</h3>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-lg p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">COPPA 合規</h3>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-lg p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900">GDPR 合規</h3>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">您覺得如何？</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            今天就試試看，親身體驗RightMaths的強大功能
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300 flex items-center justify-center space-x-2">
              <span>立即開始</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300">
              聯繫銷售
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RightMathsBrain; 